import { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import type { IconNameType } from "@src/components/Icon";
import {
  WATCH_WINDOW_MS,
  type SignalPathCounts,
  type WatchOutcome,
  type WatchReport,
} from "@visioni/a3s-monitoring/app";
import { formatTimeRemaining, watchingLabel, watchOutcomeLabel, watchReportCounts } from "../watch-text";

const OUTCOME_ICON_SIZE = 16;
/** Wide enough to read as a status stripe rather than a rendering artefact. */
const OUTCOME_EDGE_WIDTH = 3;
const WINDOW_MINUTES = Math.round(WATCH_WINDOW_MS / 60_000);

interface TestSignalWatchStatusProps {
  watching: { readonly remaining: SignalPathCounts; readonly deadline: number } | null;
  reports: readonly WatchReport[];
  onDismiss: (id: string) => void;
}

/**
 * Colour and icon per verdict.
 *
 * Both, not either: colour alone excludes anyone who cannot distinguish red
 * from green, and the icon shape carries the same verdict without it.
 */
function outcomeStyle(
  outcome: WatchOutcome,
  theme: ReturnType<typeof useUnistyles>["theme"],
): { readonly icon: IconNameType; readonly color: string } {
  const styles = {
    passed: { icon: "checkmark", color: theme.colors.status.success },
    partial: { icon: "warning", color: theme.colors.status.warning },
    failed: { icon: "error", color: theme.colors.status.error },
  } as const satisfies Record<WatchOutcome, { icon: IconNameType; color: string }>;
  return styles[outcome];
}

/**
 * Time-only, because a report is always about the last few minutes and the date
 * would be noise.
 */
function formatClosedAt(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * The only thing on the page that needs a clock every second.
 *
 * It owns its own tick so the re-render stops at this line of text. Held in the
 * hook instead, the same tick would redraw the whole page — buttons, notice and
 * every log row — three hundred times per window to move one number.
 */
function Countdown({ deadline }: { deadline: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Text textStyle="xs/regular" color="textMuted">
      Time remaining: {formatTimeRemaining(deadline - now)}
    </Text>
  );
}

/** One entry. `edgeColor` carries the verdict for entries that have one. */
function Entry({ isFirst, edgeColor, children }: { isFirst: boolean; edgeColor?: string; children: React.ReactNode }) {
  return (
    <View
      flexDirection="row"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
      gap="xl"
      p="3xl"
      borderTopWidth={isFirst ? 0 : 1}
      borderColor="borderCard"
      style={edgeColor === undefined ? undefined : { borderLeftWidth: OUTCOME_EDGE_WIDTH, borderLeftColor: edgeColor }}
    >
      {children}
    </View>
  );
}

/**
 * What the operator's presses on this camera did: still waiting, or the verdict
 * once the window closed.
 *
 * Kept out of the log because the log is a list of rows that exist, and a
 * verdict is a statement about a press — including, for a miss, about a row
 * that is not there at all.
 */
export function TestSignalWatchStatus({ watching, reports, onDismiss }: TestSignalWatchStatusProps) {
  const { theme } = useUnistyles();

  if (watching === null && reports.length === 0) return null;

  return (
    <View bg="surfaceCardInner" borderRadius={theme.spacing.lg} borderWidth={1} borderColor="borderCard">
      {watching !== null && (
        <Entry isFirst>
          <View gap="xs">
            <View flexDirection="row" alignItems="center" gap="md">
              <ActivityIndicator size="small" color={theme.colors.textMuted} />
              <Text textStyle="sm/regular" color="text">
                {watchingLabel(watching.remaining)}
              </Text>
            </View>
            <Countdown deadline={watching.deadline} />
          </View>
        </Entry>
      )}
      {reports.map((report, index) => {
        const { icon, color } = outcomeStyle(report.outcome, theme);
        return (
          <Entry key={report.id} isFirst={index === 0 && watching === null} edgeColor={color}>
            <View gap="xs">
              <View flexDirection="row" alignItems="center" gap="md">
                <Icon name={icon} size={OUTCOME_ICON_SIZE} color={color} />
                <Text textStyle="sm/regular" style={{ color }}>
                  {watchOutcomeLabel(report.outcome)}
                </Text>
              </View>
              <Text textStyle="xs/regular" color="textMuted">
                {watchReportCounts(report.outcome, report.arrived, report.missing)}
                {report.outcome === "passed" ?
                  ` · Completed at ${formatClosedAt(report.closedAt)}`
                : ` · Stopped watching at ${formatClosedAt(report.closedAt)}, ${String(WINDOW_MINUTES)} minutes after the last command`
                }
              </Text>
            </View>
            <Button label="Dismiss" variant="secondary" size="sm" onPress={() => onDismiss(report.id)} />
          </Entry>
        );
      })}
    </View>
  );
}
